import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { api } from '../api/client';
import { createSocket } from '../api/socket';
import { useAuth } from '../auth/AuthContext';

const Ctx = createContext(null);
export const useAnalyses = () => useContext(Ctx);

export function AnalysesProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [userFilter, setUserFilter] = useState(null); // nur für Admins
  const socketRef = useRef(null);

  const upsert = useCallback((patch) => {
    setItems((prev) => {
      const idx = prev.findIndex((a) => a.id === patch.id);
      if (idx === -1) return [patch, ...prev];
      const next = [...prev];
      next[idx] = { ...prev[idx], ...patch };
      return next;
    });
  }, []);

  const reload = useCallback(async (filter = userFilter) => {
    const params = filter ? { user_id: filter } : {};
    const res = await api.get('/analyses', { params });
    setItems(res.data);
  }, [userFilter]);

  useEffect(() => {
    if (!user) {
      setItems([]);
      return;
    }
    reload();

    const s = createSocket();
    socketRef.current = s;

    s.on('analysis:stage', (p) => {
      upsert({
        id: p.analysis_id,
        status: 'processing',
        _progress: p.progress_pct,
        _stage: p.stage,
        _detail: p.detail,
      });
    });

    s.on('analysis:completed', async (p) => {
      try {
        const r = await api.get(`/analyses/${p.analysis_id}`);
        upsert(r.data);
      } catch (_) {
        // Analyse wurde evtl. zwischenzeitlich gelöscht – ignorieren
      }
    });

    s.on('analysis:failed', async (p) => {
      try {
        const r = await api.get(`/analyses/${p.analysis_id}`);
        upsert(r.data);
      } catch (_) {}
    });

    s.on('connect_error', (err) => {
      console.warn('Socket-Connect-Error:', err.message);
    });

    return () => {
      s.disconnect();
      socketRef.current = null;
    };
  }, [user, reload, upsert]);

  const upload = async (file, onProgress, models) => {
    const form = new FormData();
    form.append('audioFile', file);
    if (models) {
      for (const m of models) form.append('models', m);
    }
    const res = await api.post('/analyses', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (evt) => {
        if (!onProgress || !evt.total) return;
        onProgress(Math.round((evt.loaded * 100) / evt.total));
      },
    });
    // Neues Response-Format: { primary, secondaries }
    if (res.data.primary) upsert(res.data.primary);
    if (Array.isArray(res.data.secondaries)) {
      res.data.secondaries.forEach(upsert);
    }
    return res.data;
  };

  const remove = async (id) => {
    await api.delete(`/analyses/${id}`);
    setItems((prev) => prev.filter((a) => a.id !== id));
  };

  const reanalyze = async (id, model) => {
    const res = await api.post(`/analyses/${id}/reanalyze`, { model });
    upsert(res.data);
    return res.data;
  };

  const setFilter = useCallback((uid) => {
    setUserFilter(uid);
    reload(uid);
  }, [reload]);

  return (
    <Ctx.Provider value={{ items, upload, remove, reanalyze, reload, userFilter, setFilter }}>
      {children}
    </Ctx.Provider>
  );
}
